import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CSSProperties } from "@mui/styles";
import { handleNameTranslation } from "utilities/localesUtilities";
import { EventReportsEntity } from "entities/EventReports/EventReportsEntity";
import { IOption } from "../Dropdown";
import { SegmentFieldVariant, SegmentSelect } from "./SegmentSelect";

interface IFunctionalLocationFieldProps {
    areaId?: number;
    failureTypeId?: number;
    value?: number;
    obligatory?: boolean;
    disabled?: boolean;
    inlineStyle?: CSSProperties;
    variant?: SegmentFieldVariant;
    placeholder?: string;
    onChange: (functionalLocationId: number) => void;
}

/** Controlled functional-location selector reused by the inline table edit and the split modal. */
export const FunctionalLocationField = ({
    areaId,
    failureTypeId,
    value,
    obligatory = true,
    disabled,
    inlineStyle,
    variant = "inline",
    placeholder,
    onChange,
}: IFunctionalLocationFieldProps) => {
    const { i18n } = useTranslation();
    const { loading, functionalLocations } = EventReportsEntity.GetFunctionalLocations(areaId, failureTypeId);

    const options: IOption[] = useMemo(
        () =>
            functionalLocations.map((functionalLocation) => ({
                name: handleNameTranslation(i18n.language, functionalLocation.translation.translation_names),
                id: functionalLocation.functional_location_id,
            })),
        [functionalLocations, i18n.language],
    );

    const currentSelectionInOptions = useMemo(() => options.some((option) => option.id === value), [options, value]);

    return (
        <SegmentSelect
            variant={variant}
            value={value}
            options={options}
            onChange={onChange}
            loading={loading}
            obligatory={obligatory}
            disabled={disabled ?? !areaId}
            pending={!Boolean(value) || !currentSelectionInOptions}
            placeholder={placeholder}
            inlineStyle={inlineStyle}
        />
    );
};
